import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import KkpClientAutocomplete from '../kkp/KkpClientAutocomplete';
import { AppButton } from '../ui';
import hebergementService from '../../services/hebergementService';
import { identifiantHref } from '../../utils/clientIdentifiants';

function commercialHue(name) {
  let hue = 0;
  const text = String(name || '');
  for (let index = 0; index < text.length; index += 1) {
    hue = (hue + text.charCodeAt(index) * 17) % 360;
  }
  return hue;
}

const SiteClientInlineCell = ({
  siteInfo,
  apiWorking,
  onUpdated,
  layout = 'legacy',
  onLink = null,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inputValue, setInputValue] = useState(siteInfo.clientNom ?? '');
  const [error, setError] = useState(null);
  const savingRef = useRef(false);

  useEffect(() => {
    setInputValue(siteInfo.clientNom ?? '');
  }, [siteInfo.clientNom]);

  const saveClient = async (client) => {
    if (!apiWorking || savingRef.current) return;

    const nextId = client?.id != null ? Number(client.id) : null;
    const currentId = siteInfo.dolibarrThirdpartyId != null
      ? Number(siteInfo.dolibarrThirdpartyId)
      : null;

    if (nextId === currentId) {
      setEditing(false);
      return;
    }

    if (nextId !== null && nextId <= 0) {
      setError('Ce client ne peut pas être lié (identifiant Dolibarr invalide).');
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setError(null);
    let saved = false;
    try {
      const response = await (onLink
        ? onLink(siteInfo.id, nextId)
        : hebergementService.linkClient(siteInfo.id, nextId));
      if (response.success && response.data) {
        onUpdated?.(siteInfo.id, response.data);
        setInputValue(response.data.clientNom ?? '');
        saved = true;
      } else {
        setError(response.message ?? 'Échec de la liaison client.');
      }
    } catch (err) {
      setError(err.message ?? 'Échec de la liaison client.');
      console.error('Erreur liaison client:', err);
    } finally {
      savingRef.current = false;
      setSaving(false);
      if (saved) {
        setEditing(false);
      }
    }
  };

  const cancelEditing = () => {
    if (savingRef.current) return;
    setEditing(false);
    setError(null);
    setInputValue(siteInfo.clientNom ?? '');
  };

  const handleDoubleClick = () => {
    if (!apiWorking || saving) return;
    setEditing(true);
    setError(null);
    setInputValue('');
  };

  if (editing) {
    return (
      <div style={{ minWidth: '260px' }} onDoubleClick={(e) => e.stopPropagation()}>
        <KkpClientAutocomplete
          value={inputValue}
          onChange={setInputValue}
          onSelect={saveClient}
          dolibarrOnly
          autoFocus
          size="sm"
          placeholder="Rechercher un client Dolibarr…"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelEditing();
            }
          }}
          onBlur={cancelEditing}
        />
        {(saving) && (
          <small className="text-muted d-block mt-1">Enregistrement…</small>
        )}
        {error && (
          <small className="text-danger d-block mt-1">{error}</small>
        )}
      </div>
    );
  }

  const dolibarrHref = identifiantHref({ dolibarr_id: siteInfo.dolibarrThirdpartyId }, 'dolibarr');

  if (layout === 'sites') {
    const sector = siteInfo.secteurActiviteLabel || siteInfo.sectionActiviteLabel || '';
    const commercial = siteInfo.commercialNom || '';
    const subtitle = siteInfo.clientFromSite
      ? 'Même client que le site web'
      : ([sector, commercial].filter(Boolean).join(' · ')
        || (siteInfo.dolibarrThirdpartyId ? `ID ${siteInfo.dolibarrThirdpartyId}` : ''));
    const hue = commercialHue(commercial || siteInfo.clientNom || '');
    const initials = commercial ? commercial.slice(0, 2).toUpperCase() : '';

    if (saving) {
      return <span className="sw-none">Enregistrement…</span>;
    }

    if (!siteInfo.clientNom) {
      return (
        <button type="button" className="sw-unlinked" onClick={handleDoubleClick} disabled={!apiWorking}>
          <Icon icon="lucide:link-2" width={13} />
          À relier à un client
        </button>
      );
    }

    return (
      <div className="sw-client" onDoubleClick={handleDoubleClick}>
        {initials ? (
          <span
            className="sw-avatar"
            title={commercial}
            style={{ background: `oklch(0.94 0.04 ${hue})`, color: `oklch(0.45 0.14 ${hue})` }}
          >
            {initials}
          </span>
        ) : null}
        <span className="sw-client__text">
          <span className="sw-client__name">{siteInfo.clientNom}</span>
          {subtitle ? <span className="sw-client__sub">{subtitle}</span> : null}
        </span>
        {dolibarrHref ? (
          <AppButton
            as="a"
            className="sw-ext"
            icon="lucide:external-link"
            iconWidth={13}
            href={dolibarrHref}
            target="_blank"
            rel="noopener noreferrer"
            title={`Ouvrir la fiche Dolibarr (ID ${siteInfo.dolibarrThirdpartyId})`}
            aria-label={`Ouvrir la fiche Dolibarr (ID ${siteInfo.dolibarrThirdpartyId})`}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onDoubleClick={handleDoubleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleDoubleClick();
      }}
      style={{ cursor: apiWorking ? 'pointer' : 'default' }}
    >
      {saving ? (
        <span className="text-muted small">Enregistrement…</span>
      ) : siteInfo.clientNom ? (
        <div className="d-flex align-items-center gap-2">
          <div>
            <strong>{siteInfo.clientNom}</strong>
            {siteInfo.dolibarrThirdpartyId && (
              <small className="text-muted d-block">ID {siteInfo.dolibarrThirdpartyId}</small>
            )}
          </div>
          {dolibarrHref ? (
            <AppButton
              as="a"
              variant="outline-secondary"
              size="sm"
              icon="mdi:open-in-new"
              iconWidth={16}
              href={dolibarrHref}
              target="_blank"
              rel="noopener noreferrer"
              title="Ouvrir la fiche client Dolibarr"
              aria-label="Ouvrir la fiche client Dolibarr"
              onClick={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
            />
          ) : null}
        </div>
      ) : (
        <span className="text-muted">—</span>
      )}
    </div>
  );
};

export default SiteClientInlineCell;
